/**
 * CKEditor loading policy.
 *
 * CKEditor (~200 KB) used to be fetched from the CDN on every page load via a
 * tag in index.html, even though it is only needed on the ~21 authoring screens.
 * That tag is gone; ckeditor4-vue loads the script itself the first time a
 * <ckeditor> is mounted, so the cost now falls only on pages that use one.
 *
 * Two things have to be preserved while doing that:
 *
 * 1. The version. ckeditor4-vue defaults its editorUrl prop to 4.21.0/standard-all,
 *    but index.html pinned 4.14.0/standard. Dropping the tag without pinning the
 *    prop would have been a silent major-ish upgrade to a different build, so the
 *    default is overridden below to the exact URL that was being used before.
 *
 * 2. The WIRIS maths plugin. App.vue used to call CKEDITOR.plugins.addExternal()
 *    at module scope, which only worked because the global already existed. It has
 *    to run after the namespace loads but before any editor instance is created -
 *    which is precisely when ckeditor4-vue emits 'namespaceloaded'.
 */

// Exactly what index.html loaded before, so nothing about the editor changes.
export const CKEDITOR_URL = "https://cdn.ckeditor.com/4.14.0/standard/ckeditor.js";

const WIRIS_PATH = "https://www.wiris.net/demo/plugins/ckeditor/";

let wirisRegistered = false;

/** Registers the WIRIS formula plugin once, against a loaded CKEDITOR namespace. */
export function registerWirisPlugin(namespace = window.CKEDITOR) {
  if (wirisRegistered || !namespace?.plugins?.addExternal) return;
  namespace.plugins.addExternal("ckeditor_wiris", WIRIS_PATH, "plugin.js");
  wirisRegistered = true;
}

/**
 * Installs ckeditor4-vue with the pinned editor URL, and arranges for the WIRIS
 * plugin to be registered on whichever <ckeditor> happens to mount first.
 *
 * The listener is attached through a global mixin rather than by editing all 21
 * call sites: mixin hooks run before the component's own, so the handler is in
 * place before ckeditor4-vue's mounted() emits the event.
 */
export function installCKEditor(Vue, CKEditor) {
  const editorUrlProp = CKEditor?.component?.props?.editorUrl;
  if (editorUrlProp) editorUrlProp.default = CKEDITOR_URL;

  Vue.mixin({
    beforeMount() {
      if (this.$options.name !== "ckeditor") return;
      // Fires only when the script is actually fetched. If the namespace is
      // somehow already present, the event never comes - so cover that here too.
      this.$on("namespaceloaded", (namespace) => registerWirisPlugin(namespace));
      if (window.CKEDITOR) registerWirisPlugin();
    },
  });

  Vue.use(CKEditor);
}
